import React from 'react';

import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import ModalX from '../../../BaseComponents/ModalX';

import {
	getTravellers,
	getCustomer,
	getSmsNotificationServices,
	getOrder
} from '../../../Checkout/store/order/selectors';

import SmsPopup from './components/SmsPopup';

import { GdsServiceVipSubType, OrderAdditionalServiceGdsServiceServiceType } from '@websky/graphql';
import { useTheme } from '../../../theme';
import { useToggleable } from '../../../hooks';
import { useSmsNotifications } from './SmsNotifications.hook';
import CheckoutAdditionalService from '../../../CheckoutAdditionalService/CheckoutAdditionalService/CheckoutAdditionalService';
import { ServiceSummary } from '../../../features/service-summary';
import { useRenderers } from '../../../renderProps';
import { useGdsServiceEventToggleable } from '../../../Checkout/components/Checkout/hooks';

export interface Props {
	className?: string;
	mode?: 'default' | 'aggregator';
	aggregationIcons?: React.ReactNode[];
}

const SmsNotifications: React.FC<Props> = ({ className, mode = 'default', aggregationIcons }) => {
	const { SmsNotifications: css } = useTheme('SmsNotifications');
	const { t } = useTranslation('Checkout');

	const order = useSelector(getOrder);
	const travellers = useSelector(getTravellers);
	const customer = useSelector(getCustomer);
	const smsNotificationServices = useSelector(getSmsNotificationServices);

	const { isOpen, open, close } = useToggleable(false);

	const { onSubmit, isSelected, isLoading } = useSmsNotifications(
		smsNotificationServices,
		travellers,
		order.id,
		close
	);

	useGdsServiceEventToggleable({
		services: smsNotificationServices,
		serviceTypes: [OrderAdditionalServiceGdsServiceServiceType.Vip],
		serviceSubTypes: [GdsServiceVipSubType.SMS],
		onOpen: open
	});

	const smsService = smsNotificationServices?.[0];

	const { CheckoutAdditionalService: CustomCheckoutAdditionalService } = useRenderers();
	const ServiceWrapper = CustomCheckoutAdditionalService?.CheckoutAdditionalService ?? CheckoutAdditionalService;

	if (!smsService) {
		return null;
	}

	return (
		<>
			<ServiceSummary serviceType="Sms">
				{content => (
					<ServiceWrapper
						className={className}
						addClick={open}
						buttons={[
							{
								text: isSelected ? t('Change') : t('Add'),
								action: open,
								buttonType: 'change'
							}
						]}
						header={smsService.name}
						priceFrom={smsService.price}
						description={smsService.description}
						coverClassName={css.aggregator__cover}
						mode={mode}
						isSelected={!!content}
						aggregationIcons={aggregationIcons}
						priceTotal={smsService.price}
						isLoading={isLoading}
					>
						{content}
					</ServiceWrapper>
				)}
			</ServiceSummary>

			<ModalX
				isOpen={isOpen}
				onClose={close}
				classes={{
					root: css.modal__root,
					paper: css.modal__paper
				}}
			>
				<SmsPopup
					smsNotificationId={smsService.id}
					title={smsService.name}
					description={smsService.vipFullDescription ?? smsService.description}
					travellers={travellers.filter(traveller => traveller.type !== 'INF')}
					customerValues={customer.values}
					price={smsService.price}
					onClose={close}
					onSubmit={onSubmit}
				/>
			</ModalX>
		</>
	);
};

export default SmsNotifications;
